import { bool, func, number as numberType, shape } from 'prop-types';
import React from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import { DISCRETIONARY_MEDIA } from '../../campaign-editor/constants';
import { CampaignLink } from '../../campaigns/Link';
import { commercialIncentivesOptions } from '../../constants';
import CurrencyInput from '../../generic/CurrencyInput';
import ShareInput from '../../generic/ShareInput';
import SingleSelect from '../../generic/SingleSelect';
import TimestampInfo from '../../generic/TimestampInfo';
import { getTooltipLabelWithText } from '../../generic/Tooltip';
import { campaignObjectiveOptions } from '../../opportunities/constants';
import { billableBudgetShape, quoteShape } from '../../prop-types';
import BillableBudget from '../BillableBudget';
import CommercialIncentiveModal from '../CommercialIncentiveModal';
import FlexibilityTerms from '../FlexibilityTerms';
import HELP_TEXTS from '../utils/help-texts';
import TargetKpi from '../utils/TargetKpi';
import { targetKpiOptions } from '../utils/TargetKpi/constants';

const WIDTHS = {
  main: [
    { label: 3, content: 9 },
    { label: 3, content: 9 },
  ],
  budget: { label: 3, input: 4, content: { offset: 4, size: 5 } },
  target: { label: 3, content: 9 },
  targetKpi: { label: 7, content: 5 },
};

function adjustEvent(event, name, value) {
  return { target: { ...event.target, name, value } };
}

function QuoteForm({
  model,
  invoiceSums,
  billableBudgets,
  onChange,
  newModel,
  isEditing,
  toggleCommercialIncentiveModal,
  isCommercialIncentiveModalVisible,
}) {
  const {
    opportunityId,
    campaignId,
    ownerId,
    name,
    brandIds = [],
    comment,
    targetKpi,
    targetKpiValue,
    primaryContactId,
    secondaryContactId,
    billingContactId,
    purchaseOrderNumber,
    created,
    lastModified,
    createdBy,
    lastModifiedBy,
    headerDiscountPercent,
    campaignObjective,
    commercialIncentive,
    additionalDetails,
    opportunity,
  } = model;

  const {
    grossOfferValue,
    freeServiceValue,
    feeDiscount,
    netClientBudget,
    coInvest,
    headerDiscount,
  } = invoiceSums;

  const { accountSfId } = opportunity || {};

  const readOnly = !onChange || !isEditing;
  const inputProps = {
    onChange,
    readOnly,
    disabled: !onChange || !isEditing,
  };
  const isSalesforceQuote = !!model.salesforceId;
  const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);
  const handleChangeCampaignObjective = event => {
    onChange(event);

    // Choose the first available option of the targetKpi
    // and resets targetKpiValue
    onChange({
      target: {
        name: 'targetKpi',
        value: targetKpiOptions[event.target.value][0].value || '',
      },
    });

    onChange(adjustEvent(event, 'targetKpiValue', 0));
  };

  return (
    <Row>
      <Col sm={6} className="mb-4">
        <FormGroup row>
          <Label sm={WIDTHS.main[0].label} for="name">
            Name
          </Label>
          <Col sm={WIDTHS.main[0].content}>
            <Input
              id="qu-name"
              name="name"
              type="text"
              value={name || ''}
              {...inputProps}
              readOnly={readOnly || isSalesforceQuote}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={WIDTHS.main[0].label} for="accountSfId">
            Account ID
          </Label>
          <Col sm={WIDTHS.main[0].content}>
            <Input
              id="qu-accountSfId"
              name="accountSfId"
              type="text"
              value={accountSfId || ''}
              {...inputProps}
              readOnly={readOnly || isSalesforceQuote}
              disabled
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={WIDTHS.main[0].label} for="opportunityId">
            Opportunity
          </Label>
          <Col sm={WIDTHS.main[0].content}>
            <Association
              id="qu-opportunityId"
              name="opportunityId"
              value={opportunityId}
              type="opportunity"
              {...inputProps}
              showIcon
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={WIDTHS.main[0].label} for="campaignId">
            Campaign
          </Label>
          <Col sm={WIDTHS.main[0].content}>
            {campaignId ? (
              <CampaignLink id={campaignId} />
            ) : (
              'You can create a campaign in the opportunity.'
            )}
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={WIDTHS.main[0].label} for="qu-brandIds">
            Brands
          </Label>
          <Col sm={WIDTHS.main[0].content}>
            <Association
              id="qu-brandIds"
              name="brandIds"
              value={brandIds}
              type="brand"
              {...inputProps}
              isMulti
              showIcon
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="purchaseOrderNumber"
            text="Purchase Order Nr"
            colWidth={WIDTHS.main[0].label}
            resource="qu"
          />
          <Col sm={WIDTHS.main[0].content}>
            <Input
              id="qu-purchaseOrderNumber"
              name="purchaseOrderNumber"
              type="text"
              value={purchaseOrderNumber || ''}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FlexibilityTerms
          model={model}
          labelwidth={WIDTHS.main[0].label}
          contentwidth={WIDTHS.main[0].content}
          section="qu"
          {...inputProps}
          isRequired
        />
      </Col>
      <Col sm={6} className="mb-4">
        <FormGroup row>
          <TooltipLabel
            helpKey="owner"
            text="Owner"
            colWidth={WIDTHS.main[1].label}
            resource="qu"
          />
          <Col sm={WIDTHS.main[1].content}>
            <Association
              id="qu-ownerId"
              name="ownerId"
              value={ownerId}
              type="user"
              {...inputProps}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={WIDTHS.main[1].label} for="primaryContactId">
            Primary Contact
          </Label>
          <Col sm={WIDTHS.main[1].content}>
            <Association
              id="qu-primaryContactId"
              name="primaryContactId"
              value={primaryContactId}
              type="contact"
              {...inputProps}
              verbose
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={WIDTHS.main[1].label} for="secondaryContactId">
            Secondary Contact
          </Label>
          <Col sm={WIDTHS.main[1].content}>
            <Association
              id="qu-secondaryContactId"
              name="secondaryContactId"
              value={secondaryContactId}
              type="contact"
              {...inputProps}
              verbose
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={WIDTHS.main[1].label} for="billingContactId">
            Billing Contact
          </Label>
          <Col sm={WIDTHS.main[1].content}>
            <Association
              id="qu-billingContactId"
              name="billingContactId"
              value={billingContactId}
              type="contact"
              {...inputProps}
              verbose
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="comment"
            text="Notes"
            colWidth={WIDTHS.main[1].label}
            resource="qu"
          />
          <Col sm={WIDTHS.main[1].content}>
            <Input
              id="qu-comment"
              name="comment"
              type="textarea"
              value={comment || ''}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={WIDTHS.main[1].label} for="qu-commercialIncentive">
            Commercial Incentive
          </Label>
          <Col sm={WIDTHS.main[1].content}>
            <SingleSelect
              options={commercialIncentivesOptions}
              id="qu-commercialIncentive"
              name="commercialIncentive"
              value={commercialIncentive}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        {commercialIncentive === DISCRETIONARY_MEDIA && (
          <FormGroup row>
            <TooltipLabel
              helpKey="additionalDetails"
              text="Additional Details"
              colWidth={WIDTHS.main[1].label}
              resource="qu"
              className="required"
            />
            <Col sm={WIDTHS.main[1].content}>
              <Input
                id="qu-additionalDetails"
                name="additionalDetails"
                value={additionalDetails}
                type="textarea"
                {...inputProps}
              />
            </Col>
          </FormGroup>
        )}
        <CommercialIncentiveModal
          commercialIncentive={commercialIncentive}
          additionalDetails={additionalDetails}
          isCommercialIncentiveModalVisible={isCommercialIncentiveModalVisible}
          onChange={onChange}
          toggleCommercialIncentiveModal={toggleCommercialIncentiveModal}
        />
      </Col>

      <Col sm={6}>
        <h4>Budget Overview</h4>
        <FormGroup row>
          <TooltipLabel
            helpKey="grossOfferValue"
            text="Gross Offer Value"
            colWidth={WIDTHS.main[0].label}
            resource="qu"
          />
          <Col sm={WIDTHS.budget.content}>
            <CurrencyInput
              id="qu-grossOfferValue"
              name="grossOfferValue"
              value={grossOfferValue || 0}
              readOnly
            />
          </Col>
        </FormGroup>

        <FormGroup row>
          <TooltipLabel
            helpKey="feeDiscount"
            text="Fee Discount"
            colWidth={WIDTHS.main[0].label}
            resource="qu"
          />
          <Col sm={WIDTHS.budget.content}>
            <CurrencyInput
              id="qu-feeDiscount"
              name="feeDiscount"
              value={feeDiscount || 0}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="coInvest"
            text="Co-Invest used"
            colWidth={WIDTHS.budget.label}
            resource="qu"
          />
          <Col sm={WIDTHS.budget.content}>
            <CurrencyInput
              id="qu-coInvest"
              name="coInvest"
              value={coInvest || 0}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="headerDiscount"
            text="Header Discount"
            colWidth={WIDTHS.budget.label}
            resource="qu"
          />
          <Col sm={WIDTHS.budget.input}>
            <ShareInput
              id="qu-headerDiscountPercent"
              name="headerDiscountPercent"
              type="text"
              value={headerDiscountPercent || 0}
              {...inputProps}
            />
          </Col>
          <Col sm={WIDTHS.budget.content.size}>
            <CurrencyInput
              id="qu-headerDiscountAbsolute"
              name="headerDiscountAbsolute"
              value={headerDiscount}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="freeServiceValue"
            text="Free Service Value"
            colWidth={WIDTHS.budget.label}
            resource="qu"
          />
          <Col sm={WIDTHS.budget.content}>
            <CurrencyInput
              id="qu-freeServiceValue"
              name="freeServiceValue"
              value={freeServiceValue || 0}
              readOnly
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="netClientBudget"
            text="Net Client Budget"
            colWidth={WIDTHS.budget.label}
            resource="qu"
          />
          <Col sm={WIDTHS.budget.content}>
            <CurrencyInput
              id="qu-netClientBudget"
              name="netClientBudget"
              value={netClientBudget || 0}
              readOnly
            />
          </Col>
        </FormGroup>
      </Col>

      <Col sm={6}>
        <h4>Target & Objective</h4>
        <FormGroup row>
          <Label
            sm={WIDTHS.main[0].label}
            for="qu-campaignObjective"
            className="required"
          >
            Campaign Objective
          </Label>
          <Col sm={WIDTHS.main[0].content}>
            <SingleSelect
              options={campaignObjectiveOptions}
              id="qu-campaignObjective"
              name="campaignObjective"
              value={campaignObjective}
              {...inputProps}
              onChange={handleChangeCampaignObjective}
            />
          </Col>
        </FormGroup>
        <TargetKpi
          {...inputProps}
          widths={WIDTHS.targetKpi}
          targetKpi={targetKpi}
          targetKpiValue={targetKpiValue}
          campaignObjective={campaignObjective}
        />
        <FormGroup className="mt-5">
          <BillableBudget billableBudgets={billableBudgets} />
        </FormGroup>
        {!newModel && (
          <FormGroup row>
            <Col sm={12}>
              <TimestampInfo
                timestamp={created}
                actionName="created"
                author={createdBy}
                displayAttribute="inline"
              />
              <TimestampInfo
                timestamp={lastModified}
                actionName="lastModified"
                author={lastModifiedBy}
                displayAttribute="inline"
              />
            </Col>
          </FormGroup>
        )}
      </Col>
    </Row>
  );
}

QuoteForm.propTypes = {
  model: quoteShape.isRequired,
  invoiceSums: shape({
    grossOfferValue: numberType.isRequired,
    freeServiceValue: numberType.isRequired,
    feeDiscount: numberType.isRequired,
    netClientBudget: numberType.isRequired,
    coInvest: numberType.isRequired,
    headerDiscount: numberType.isRequired,
  }).isRequired,
  billableBudgets: billableBudgetShape.isRequired,
  onChange: func,
  onSubmit: func,
  toggleCommercialIncentiveModal: func,
  newModel: bool,
  onIOWithdrawal: func,
  isEditing: bool,
  isCommercialIncentiveModalVisible: bool,
};

QuoteForm.defaultProps = {
  onChange: undefined,
  onSubmit: undefined,
  toggleCommercialIncentiveModal: undefined,
  onIOWithdrawal: undefined,
  newModel: false,
  isEditing: true,
  isCommercialIncentiveModalVisible: false,
};

export default QuoteForm;
